import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight, CheckCircle2, RotateCw } from "lucide-react";

const URGENT = new Set(["comparison_failed"]);

const HEADLINE = {
  photos_missing: (a) => `Action required: ${a.detail}`,
  comparison_failed: () => "A comparison needs a retry",
  awaiting_signature: () => "Awaiting your landlord's signature",
  generate_pdf: () => "Finish your dispute letter",
  deadline_upcoming: (a) => a.detail,
  setup_deadline_tracking: () => "Set up deposit deadline tracking",
};

export function NextActionHero({ action, loading, error, onRetry, onViewProperties }) {
  const navigate = useNavigate();

  if (loading) {
    return <div data-testid="next-action-loading" className="rounded-3xl border border-[#E2E8F0] bg-white/60 h-52 animate-pulse" />;
  }

  if (error) {
    return (
      <div data-testid="next-action-error" className="rounded-3xl border border-[#E2E8F0] bg-white p-8 flex flex-wrap items-center justify-between gap-4">
        <p className="font-heading text-lg font-bold text-navy">Unable to load your status</p>
        <Button data-testid="next-action-retry" onClick={onRetry} variant="outline" className="rounded-xl">
          <RotateCw size={16} className="mr-2" /> Retry
        </Button>
      </div>
    );
  }

  if (!action) return null;

  if (action.type === "all_clear") {
    return (
      <div data-testid="next-action-hero" className="rounded-3xl border border-[#E2E8F0] bg-white p-8 sm:p-12">
        <div className="flex items-center gap-2 text-[#177245]">
          <CheckCircle2 size={22} />
          <span className="text-xs font-bold uppercase tracking-[0.18em]">All clear</span>
        </div>
        <h1 data-testid="next-action-headline" className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-navy">
          You're all set.
        </h1>
        <p className="mt-3 text-[#475569] text-base sm:text-lg">{action.message}</p>
        <button data-testid="next-action-cta" onClick={onViewProperties} className="mt-6 inline-flex items-center gap-1.5 font-semibold text-brand hover:underline">
          View your properties <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  const urgent = URGENT.has(action.type);
  const block = urgent ? "bg-[#7F1D1D] text-white" : "bg-navy text-white";
  const btn = urgent ? "bg-white text-[#B91C1C] hover:bg-white/90" : "bg-brand text-white hover:bg-brand-hover";
  const headline = (HEADLINE[action.type] || ((a) => a.detail))(action);

  return (
    <div data-testid="next-action-hero" className={`rounded-3xl p-8 sm:p-12 ${block}`}>
      <div className="flex items-center gap-2 opacity-90">
        {urgent && <AlertTriangle size={20} />}
        <span className="text-xs font-bold uppercase tracking-[0.18em]">{urgent ? "Needs attention" : "Your next step"}</span>
      </div>
      <h1 data-testid="next-action-headline" className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
        {headline}
      </h1>
      {action.property_address && (
        <p className="mt-4 text-base sm:text-lg opacity-80">{action.property_address}</p>
      )}
      {action.cta_label && (
        <Button data-testid="next-action-cta" onClick={() => navigate(action.cta_route)} className={`mt-7 h-14 px-8 text-base rounded-xl font-bold ${btn}`}>
          {action.cta_label} <ArrowRight size={18} className="ml-2" />
        </Button>
      )}
    </div>
  );
}
